import React from 'react';
import type { CardLinkAsset, ConciergeTileAsset, PriceValuePropAsset, PropAsset, PropCarouselAsset } from '../../propValues.types';
import { CardTileConcierge } from './components/CardTileConcierge';
import { CardLink } from './components/CardLink';
import type { CardTileSource } from './components/CardTile.types';
import { CardTileShell } from './components/CardTileShell';
import { ValuePropCard } from './components/ValueProps/ValuePropCard';
import { ValuePropListCard } from './components/ValueProps/ValuePropListCard';
import { PriceValuePropCard } from './components/PriceValuePropCard';
import { isConciergeServiceCategory } from '@apps/card/routes/Concierge/config';
import type { StationeryTemplateCategoryEnum } from '@graphql/generated-base';
import type { CardFormat } from '../../../ProductDetailsPage/constants';
import type { StationeryTemplate } from '../../DesignsGallery.types';

export type ConciergeSelectedEvent = {
  id: string;
};

export type CardEventType = ConciergeSelectedEvent & { type: 'conciergeselectedevent' };

export type GalleryTemplate = StationeryTemplate & {
  type: 'template';
  // Set when the gallery-order feature flag repeats a template with a specific
  // colorway (search inactive only). `colorwayOverride` pre-selects that colorway;
  // `instanceKey` gives each repeated tile a stable, unique React key.
  colorwayOverride?: string;
  instanceKey?: string;
};
export type GalleryValueProp = PropAsset & { type: 'prop' };
export type GalleryValuePropCarousel = PropCarouselAsset & { type: 'propCarousel' };

export type GalleryItemData =
  | GalleryValueProp
  | GalleryValuePropCarousel
  | GalleryTemplate
  | (CardLinkAsset & { type: 'cardlink' })
  | (ConciergeTileAsset & { type: 'concierge' })
  | (PriceValuePropAsset & { type: 'priceValueProp' });

type GalleryItemProps = {
  preferredFormat?: CardFormat;
  eventId?: string;
  data: GalleryItemData;
  onCardSelect: (event: CardEventType) => void;
  hideCardTileTag?: boolean;
  cardTileSource: CardTileSource;
  currentCategory: StationeryTemplateCategoryEnum;
  /** Position in the gallery; the first rows' static tiles load their image eagerly. */
  index?: number;
};

export const GalleryItem = (props: GalleryItemProps) => {
  const { data, preferredFormat, cardTileSource, onCardSelect, eventId, hideCardTileTag, currentCategory, index } = props;
  switch (data.type) {
    case 'prop':
      return <ValuePropCard data={data} />;
    case 'propCarousel':
      return <ValuePropListCard data={data} />;
    case 'priceValueProp':
      return <PriceValuePropCard data={data} />;
    case 'cardlink':
      return <CardLink data={data} />;
    case 'template':
      return (
        <CardTileShell
          cardTileSource={cardTileSource}
          data={data}
          hideTag={hideCardTileTag}
          eventId={eventId}
          preferredFormat={preferredFormat}
          currentCategory={currentCategory}
          index={index}
        />
      );
    case 'concierge':
      if (isConciergeServiceCategory(currentCategory)) {
        return (
          <CardTileConcierge
            category={currentCategory}
            data={data}
            onSelect={onCardSelect}
          />
        );
      }
      return null;
  }
};
